'use client';

import React, { useState, useCallback } from 'react';
import AppLayout from '@/components/AppLayout';
import RateCalculatorForm from './components/RateCalculatorForm';
import PastCalculationsSidebar from './components/PastCalculationsSidebar';
import ToastProvider from '@/components/ui/Toast';
import type { RateInput } from '@/lib/rateCalculator';
import { Zap, TrendingUp, Users, Star } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import Icon from '@/components/ui/AppIcon';


export default function RateCalculatorPage() {
  const [historyRefresh, setHistoryRefresh] = useState(0);
  const [restoreInput, setRestoreInput] = useState<RateInput | null>(null);
  const { t } = useLanguage();

  const statsHighlights = [
    { icon: Users, value: '14K+', label: t.home.stat_creators },
    { icon: TrendingUp, value: '$2.4M', label: t.home.stat_rates },
    { icon: Star, value: '4.9★', label: t.home.stat_rating },
  ];

  const handleCalculated = useCallback(() => {
    setHistoryRefresh((n) => n + 1);
  }, []);

  const handleRestore = useCallback((input: RateInput) => {
    setRestoreInput({ ...input });
  }, []);

  return (
    <AppLayout>
      <ToastProvider />
      <div className="max-w-screen-2xl mx-auto px-4 md:px-6 xl:px-10 2xl:px-14 py-10">
        {/* Page Header */}
        <div className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/10 text-primary text-xs font-bold uppercase tracking-widest mb-4">
            <Zap size={12} />
            {t.home.badge}
          </div>
          <h1 className="text-4xl xl:text-5xl font-extrabold text-foreground leading-tight">
            {t.home.headline1}{' '}
            <span className="text-gradient-primary">{t.home.headline2}</span>
          </h1>
          <p className="text-muted-foreground text-base xl:text-lg mt-3 max-w-xl mx-auto">
            {t.home.subtitle}
          </p>

          {/* Stats Row */}
          <div className="flex items-center justify-center gap-6 mt-6 flex-wrap">
            {statsHighlights?.map((stat) => {
              const Icon = stat?.icon;
              return (
                <div key={`stat-${stat?.label}`} className="flex items-center gap-2">
                  <Icon size={14} className="text-accent" />
                  <span className="text-sm font-bold text-foreground font-tabular">{stat?.value}</span>
                  <span className="text-xs text-muted-foreground">{stat?.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Main layout: sidebar + calculator */}
        <div className="flex gap-6 xl:gap-8 items-start">
          {/* Past Calculations Sidebar */}
          <div className="hidden xl:flex flex-col w-64 2xl:w-72 flex-shrink-0 sticky top-24 self-start card-elevated overflow-hidden max-h-[calc(100vh-8rem)]">
            <PastCalculationsSidebar
              onRestore={handleRestore}
              refreshTrigger={historyRefresh}
            />
          </div>

          {/* Calculator */}
          <div className="flex-1 min-w-0">
            <RateCalculatorForm
              onCalculated={handleCalculated}
              restoreInput={restoreInput}
            />

            {/* Mobile history panel */}
            <div className="xl:hidden mt-6 card-elevated overflow-hidden max-h-96">
              <PastCalculationsSidebar
                onRestore={handleRestore}
                refreshTrigger={historyRefresh}
              />
            </div>
          </div>
        </div>

        {/* Bottom Note */}
        <div className="mt-8 text-center">
          <p className="text-xs text-muted-foreground">
            {t.home.cta_account}{' '}
            <a href="/sign-up-login" className="text-primary font-semibold hover:underline">
              {t.home.cta_link}
            </a>{' '}
            {t.home.cta_suffix}
          </p>
        </div>
      </div>
    </AppLayout>
  );
}